import React, { useState, useEffect } from 'react';
import FileCard from '../components/FileCard';
import FileUploadForm from '../components/FileUploadForm';
import FileDownloadForm from '../components/FileDownloadForm';

const UploadDownloadPage = () => {
  const [files, setFiles] = useState([]);
  const [fileUploadLimit, setFileUploadLimit] = useState(25 * 1024 * 1024); // 25MB
  const [previewUrl, setPreviewUrl] = useState('');
  const [nonCurrentAndOtherFiles, setNonCurrentAndOtherFiles] = useState([]);
  const [showNonCurrentAndOtherFiles, setShowNonCurrentAndOtherFiles] = useState(false);

  const fetchFiles = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/files/existing-files`, {
        method: 'GET',
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      console.log('Fetched files:', data);
      setFiles(data);
    } catch (error) {
      console.error('Error fetching files:', error);
    }
  };

  useEffect(() => {
    fetchFiles();
  }, []);

  const fetchNonCurrentAndOtherFiles = async () => {
    try {
      console.log('Fetching non-current and other files...');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/files/non-current-and-other-files`, {
        method: 'GET',
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      // Filter out files with "N/A" documentName
      const filteredData = data.filter(file => file.documentName !== 'N/A' && file.documentName !== '');
      setNonCurrentAndOtherFiles(filteredData);
      console.log('Fetched Non-Current and Other Files:', filteredData);
    } catch (error) {
      console.error('Error fetching non-current and other files:', error);
    }
  };


  useEffect(() => {
  
    if (showNonCurrentAndOtherFiles) {
      fetchNonCurrentAndOtherFiles();
    }
  }, [showNonCurrentAndOtherFiles]);



const handleUpload = async ({ file, documentType, documentNumber }) => {
  try {
      if (file.size > fileUploadLimit) {
          alert(`File size exceeds the limit of ${fileUploadLimit / (1024 * 1024)} MB.`);
          return;
      }

      const fileName = getFileNameFromDocumentType(documentType, file.name);
      const contentType = file.type || 'application/octet-stream';
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/files/generate-upload-url`, {
          method: 'POST',
          credentials: 'include',
          headers: {
              'Content-Type': 'application/json',
          },
          body: JSON.stringify({ fileName, documentType, documentNumber, contentType }),
      });
      const { url } = await response.json();

      const uploadResponse = await fetch(url, {
          method: 'PUT',
          headers: {
              'Content-Type': contentType,
          },
          body: file,
      });

      if (uploadResponse.ok) {
          // Only call update-metadata if the upload was successful
          await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/files/update-metadata`, {
              method: 'POST',
              credentials: 'include',
              headers: {
                  'Content-Type': 'application/json',
              },
              body: JSON.stringify({ fileName, documentType, documentNumber, contentType })
          });


          alert('File uploaded successfully!');

          // Fetch the updated list of existing files
          await fetchFiles();
          await fetchNonCurrentAndOtherFiles();
          
      } else {
          alert('Failed to upload the file.');
      }
  } catch (error) {
      console.error('Error uploading file:', error);
      alert('Error uploading file.');
  }
};



const handleDownload = async (documentName) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/files/generate-download-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ fileName: documentName }),
      });
      const { url } = await response.json();
  
      // Create a link and trigger the download
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', documentName); // This attribute prompts download
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Error downloading file.');
    }
  };
  

  const handlePreview = async (documentName) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/files/generate-preview-url`, {
        method: 'POST', 
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ fileName: documentName }),
      });
      const { url } = await response.json();

      // Triggering preview
    //   window.open(url, '_blank'); // previewing in a different window
    setPreviewUrl(url);
    } catch (error) {
      console.error('Error previewing file:', error);
      alert('Error previewing file.');
    }
  };

  const getFileNameFromDocumentType = (documentType, originalFileName) => {
    switch (documentType) {
      case 'EIN':
        return 'EIN';
      case 'SBA':
        return 'SBA';
      case 'SBC':
        return 'SBC';
      case 'COF':
        return 'COF';
      case 'OTH':
        return originalFileName;
      default:
        return '';
    }
  };


  const getLatestFiles = (files) => {
    const latestFiles = {
      EIN: null,
      SBA: null,
      SBC: null,
      UEI: null,
      COF: null,
    };

    files.forEach((file) => {
      const { documentType, documentName, lastModified, contentType } = file;

      if (documentName === 'EIN' || documentName === 'SBA' || documentName === 'SBC' || documentName === 'COF' || documentName === 'UEI') {
        if (!latestFiles[documentName] || lastModified > latestFiles[documentName].lastModified) {
          latestFiles[documentName] = file;
        }
      }
    });

    console.log(latestFiles)

    return Object.values(latestFiles).filter(Boolean);
  };


  const latestFiles = getLatestFiles(files);

  
  return (
    <div>
        <h1>Upload and Download Files</h1>
        <div>
        <h2>Existing Files</h2>
        {files.length > 0 ? (
          files.map((file, index) => (
            <div key={index}>
              <FileCard file={file} />
              <button onClick={() => handlePreview(file.documentName)}>Preview</button>
              <button onClick={() => handleDownload(file.documentName)}>Download</button>
            </div>
          ))
        ) : (
          <p>No files uploaded yet.</p>
        )}
        <button onClick={() => setShowNonCurrentAndOtherFiles(!showNonCurrentAndOtherFiles)}>
          {showNonCurrentAndOtherFiles ? 'Hide Non-Current Versions and Other Files' : 'View Non-Current Versions and Other Files'}
        </button>
        {showNonCurrentAndOtherFiles && (
          <div>
            <h3>Non-Current Versions and Other Files</h3>
            {nonCurrentAndOtherFiles
              .filter(file => file.documentName !== 'N/A' && file.documentName !== '')
              .map((file, index) => (
                <div key={index}>
                  <FileCard file={file} />
                  <button onClick={() => handlePreview(file.documentName)}>Preview</button>
                  <button onClick={() => handleDownload(file.documentName)}>Download</button>
                </div>
              ))}
          </div>
        )}
            {/* <h2>Existing Files</h2>
            {files.length > 0 ? (
                files.map((file, index) => (
                    <div key={index}>
                        <FileCard file={file} />
                        <button onClick={() => handlePreview(file.documentName)}>Preview</button>
                        <button onClick={() => handleDownload(file.documentName)}>Download</button>
                    </div>
                ))
            ) : (
                <p>No files uploaded yet.</p>
            )}
            <NonCurrentVersionFiles files={files} onDownload={handleDownload} onPreview={handlePreview}/> */}
        </div>
        {previewUrl && (
        <div>
            <h2>Preview</h2>
            {['jpg', 'jpeg', 'png', 'gif'].includes(previewUrl.split('.').pop().toLowerCase()) ? (
            <img src={previewUrl} alt="Document Preview" style={{ maxWidth: '100%', maxHeight: '500px' }} />
            ) : (
            <iframe src={previewUrl} title="Document Preview" style={{ width: '100%', height: '500px' }}></iframe>
            )}
        </div>
        )}

        <div>
            <h2>Upload File</h2>
            <FileUploadForm onUpload={handleUpload} documentTypeOptions={[
                { value: 'EIN', label: 'Employer Identification Number (EIN)' },
                { value: 'SBA', label: 'Small Business Administration (SBA)' },
                { value: 'SBC', label: 'SBC.gov SBC Registration (SBC)' },
                { value: 'UEI', label: 'Unique Entity Identifier (UEI)' },
                { value: 'COF', label: 'Certificate of Formation (COF)' },
                { value: 'OTH', label: 'Other' },
            ]} />
        </div>
    </div>
);
};  

export default UploadDownloadPage;
